import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore'

// Ajuste aqui se voce ja tiver um arquivo central de config de API.
// @ts-ignore - Vite injeta env vars em runtime; esse projeto nao tem os tipos do vite/client declarados
const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || 'http://127.0.0.1:8787/api'
// As landing pages (/lp/:slug) sao servidas na raiz do worker, fora do prefixo /api
const WORKER_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, '')

interface Variation {
  id: number
  headline: string
  subheadline: string
  body: string
  cta: string
  angle: string
}

interface LandingPage {
  slug: string
  url: string
}

interface Campaign {
  id: string
  status: string
  briefing: {
    productName: string
    targetAudience: string
    objective: string
    segment: string
    whatsapp?: string
  }
  selectedVariation: Variation
  allVariations: Variation[]
  createdAt: any
  landingPage?: LandingPage
}

export default function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [campaign, setCampaign] = useState<Campaign | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [creatingLanding, setCreatingLanding] = useState(false)
  const [landingError, setLandingError] = useState('')
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    async function load() {
      if (!id) return
      try {
        const db = getFirestore()
        const snap = await getDoc(doc(db, 'campaigns', id))
        if (!snap.exists()) {
          setError('Campanha não encontrada.')
          return
        }
        setCampaign({ id: snap.id, ...snap.data() } as Campaign)
      } catch (err: any) {
        setError('Erro ao carregar campanha: ' + err.message)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [id])

  async function handleCreateLandingPage() {
    if (!campaign) return
    setCreatingLanding(true)
    setLandingError('')

    try {
      const v = campaign.selectedVariation
      const res = await fetch(`${API_BASE_URL}/landing/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaignId: campaign.id,
          productName: campaign.briefing.productName,
          targetAudience: campaign.briefing.targetAudience,
          headline: v.headline,
          subheadline: v.subheadline,
          body: v.body,
          cta: v.cta,
          // Numero especifico dessa campanha. O worker usa ele para montar
          // o link de redirecionamento (wa.me) depois da captura do lead.
          whatsappNumber: campaign.briefing.whatsapp || '',
        }),
      })

      const data = await res.json()

      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Erro ao gerar landing page')
      }

      const landingPage: LandingPage = { slug: data.slug, url: data.url }

      const db = getFirestore()
      await updateDoc(doc(db, 'campaigns', campaign.id), { landingPage })

      setCampaign({ ...campaign, landingPage })
    } catch (err: any) {
      setLandingError(err.message || 'Erro ao gerar landing page. Tente novamente.')
    } finally {
      setCreatingLanding(false)
    }
  }

  function handleCopyLink() {
    if (!campaign?.landingPage) return
    const fullUrl = `${WORKER_ORIGIN}${campaign.landingPage.url}`
    navigator.clipboard.writeText(fullUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Carregando campanha...</div>
      </div>
    )
  }

  if (error || !campaign) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg px-4 py-3">
          {error || 'Campanha não encontrada.'}
        </div>
      </div>
    )
  }

  const v = campaign.selectedVariation

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button onClick={() => navigate('/campanhas')} className="text-gray-400 hover:text-white transition-colors">
          ← Voltar
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white">{campaign.briefing.productName}</h1>
          <span className="text-xs px-2 py-1 rounded-full bg-yellow-500/20 text-yellow-400 font-medium">
            {campaign.status === 'draft' ? 'Rascunho' : campaign.status}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Variação selecionada */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white/5 border border-indigo-500/40 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs font-medium px-2 py-1 rounded-full bg-indigo-500/20 text-indigo-300">
                Variação selecionada
              </span>
              <span className="text-xs font-medium px-2 py-1 rounded-full bg-white/10 text-gray-300">
                {v.angle}
              </span>
            </div>
            <h2 className="text-white font-bold text-2xl mb-2">{v.headline}</h2>
            <p className="text-gray-300 mb-3">{v.subheadline}</p>
            <p className="text-gray-400 mb-4">{v.body}</p>
            <span className="inline-block px-5 py-2 rounded-lg text-sm font-semibold text-white"
              style={{background:'linear-gradient(135deg,#6366f1,#10b981)'}}>
              {v.cta}
            </span>
          </div>

          {/* Outras variações */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <h3 className="text-white font-semibold mb-4">Outras variações geradas</h3>
            <div className="space-y-3">
              {campaign.allVariations
                .filter(av => av.id !== v.id)
                .map(av => (
                  <div key={av.id} className="border border-white/10 rounded-xl p-4 bg-white/3">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-gray-400">{av.angle}</span>
                    </div>
                    <p className="text-white font-medium text-sm">{av.headline}</p>
                    <p className="text-gray-400 text-xs mt-1">{av.subheadline}</p>
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* Briefing */}
        <div className="space-y-6">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <h3 className="text-white font-semibold mb-4">Briefing</h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Produto</p>
                <p className="text-white text-sm">{campaign.briefing.productName}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Público-alvo</p>
                <p className="text-white text-sm">{campaign.briefing.targetAudience}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Objetivo</p>
                <p className="text-white text-sm">{campaign.briefing.objective}</p>
              </div>
              {campaign.briefing.segment && (
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Segmento</p>
                  <p className="text-white text-sm">{campaign.briefing.segment}</p>
                </div>
              )}
              {campaign.briefing.whatsapp && (
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">WhatsApp</p>
                  <p className="text-white text-sm">{campaign.briefing.whatsapp}</p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <h3 className="text-white font-semibold mb-4">Próximos passos</h3>
            <div className="space-y-4">

              {/* Item: Criar landing page */}
              <div className="flex gap-3 items-start">
                <div
                  className={`w-5 h-5 rounded-full border flex-shrink-0 mt-0.5 ${
                    campaign.landingPage ? 'bg-emerald-500 border-emerald-500' : 'border-white/20'
                  }`}
                />
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">Criar landing page</p>
                  <p className="text-gray-500 text-xs mb-2">Gere a página de captura automaticamente</p>

                  {!campaign.landingPage && (
                    <button
                      onClick={handleCreateLandingPage}
                      disabled={creatingLanding}
                      className="w-full py-2 rounded-lg text-sm font-semibold text-white transition-opacity disabled:opacity-60"
                      style={{ background: 'linear-gradient(135deg,#6366f1,#10b981)' }}
                    >
                      {creatingLanding ? 'Gerando...' : 'Criar landing page'}
                    </button>
                  )}

                  {landingError && (
                    <p className="text-red-400 text-xs mt-2">{landingError}</p>
                  )}

                  {campaign.landingPage && (
                    <div className="bg-white/5 border border-white/10 rounded-lg p-3 mt-1 space-y-2">
                      <p className="text-emerald-400 text-xs font-medium">Landing page criada ✓</p>
                      <p className="text-gray-400 text-xs break-all">
                        {WORKER_ORIGIN}{campaign.landingPage.url}
                      </p>
                      <div className="flex gap-2">
                        <a
                          href={`${WORKER_ORIGIN}${campaign.landingPage.url}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex-1 text-center py-1.5 rounded-md text-xs font-medium bg-white/10 text-white hover:bg-white/15 transition-colors"
                        >
                          Abrir
                        </a>
                        <button
                          onClick={handleCopyLink}
                          className="flex-1 text-center py-1.5 rounded-md text-xs font-medium bg-white/10 text-white hover:bg-white/15 transition-colors"
                        >
                          {copied ? 'Copiado!' : 'Copiar link'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Demais itens (ainda nao implementados) */}
              {[
                { t: 'Configurar WhatsApp', d: 'Ative o follow-up automático' },
                { t: 'Publicar anúncio', d: 'Suba no Meta ou Google Ads' },
              ].map((item, i) => (
                <div key={i} className="flex gap-3 items-start">
                  <div className="w-5 h-5 rounded-full border border-white/20 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white text-sm font-medium">{item.t}</p>
                    <p className="text-gray-500 text-xs">{item.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
